import {
  CONSENT_CATEGORIES,
  CONSENT_DENIED,
  CONSENT_GRANTED,
  consentModePayload,
  pushConsentUpdate,
  readConsentCookie,
  shouldLoadTag,
  writeConsentCookie,
} from './consent';

const ROOT = '[data-hc-consent]';
const GTM_SCRIPT_ID = 'hc-gtm';
const GTM_URL = 'https://www.googletagmanager.com/gtm.js';

let opener = null;

const partsOf = root => ({
  banner: root.querySelector('[data-hc-consent-banner]'),
  panel: root.querySelector('[data-hc-consent-panel]'),
  dismiss: root.querySelector('[data-hc-consent-dismiss]'),
});

const measures = choices => choices.analytics || choices.marketing;

export const tagManagerLoaded = () =>
  Boolean(document.getElementById(GTM_SCRIPT_ID));

export const loadTagManager = (containerId, choices) => {
  if (tagManagerLoaded() || !measures(choices)) return;
  if (!shouldLoadTag(containerId, window.location.hostname)) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      // Tag Manager reads a gtag command only as an Arguments object, never as an array.
      window.dataLayer.push(arguments);
    };
  window.gtag('consent', 'default', consentModePayload(choices));
  window.gtag('set', 'ads_data_redaction', !choices.marketing);
  window.gtag('set', 'url_passthrough', true);
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `${GTM_URL}?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);
};

const visible = element => element?.isConnected && !element.closest('[hidden]');

const closePanel = root => {
  const { panel } = partsOf(root);
  if (panel.open) panel.close();
};

const commit = (root, choices) => {
  writeConsentCookie(choices);
  if (tagManagerLoaded()) pushConsentUpdate(choices);
  else loadTagManager(root.dataset.hcConsent, choices);
  partsOf(root).banner.hidden = true;
  closePanel(root);
};

const panelChoices = root =>
  Object.fromEntries(
    CONSENT_CATEGORIES.map(category => [
      category,
      Boolean(
        root.querySelector(`[data-hc-consent-category="${category}"]`)?.checked
      ),
    ])
  );

const openPanel = (root, trigger) => {
  const { banner, panel, dismiss } = partsOf(root);
  const record = readConsentCookie();
  const choices = record ? record.categories : CONSENT_DENIED;
  panel.querySelectorAll('[data-hc-consent-category]').forEach(box => {
    box.checked = choices[box.dataset.hcConsentCategory] === true;
  });
  dismiss.hidden = !record;
  banner.hidden = true;
  opener = trigger;
  if (!panel.open) panel.showModal();
};

const onPanelClose = root => {
  if (!readConsentCookie()) partsOf(root).banner.hidden = false;
  if (visible(opener)) opener.focus();
  opener = null;
};

const ACTIONS = {
  accept: root => commit(root, CONSENT_GRANTED),
  reject: root => commit(root, CONSENT_DENIED),
  save: root => commit(root, panelChoices(root)),
  open: (root, button) => openPanel(root, button),
  close: root => closePanel(root),
};

const onClick = event => {
  const button = event.target.closest('[data-hc-consent-action]');
  const root = button && document.querySelector(ROOT);
  if (!root) return;
  ACTIONS[button.dataset.hcConsentAction]?.(root, button);
};

export const initConsentControls = () => {
  document.addEventListener('click', onClick);
};

export const initConsent = () => {
  const root = document.querySelector(ROOT);
  if (!root) return;

  const { banner, panel } = partsOf(root);
  panel.addEventListener('close', () => onPanelClose(root));
  const record = readConsentCookie();
  if (record) loadTagManager(root.dataset.hcConsent, record.categories);
  else banner.hidden = false;
};
